import React, { useState, useCallback, useMemo } from 'react';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { StatCard } from '../../components/common/StatCard';
import { ChainPicker, Chain, loadChain, saveChain } from '../../components/common/ChainPicker';
import { useStaggeredEntrance } from '../../hooks/useStaggeredEntrance';
import { ForgeStatCard } from '../../types';

const SYSTEM_SERVICES = [
  { name: 'API Status', status: 'Healthy' },
  { name: 'MongoDB', status: 'Connected' },
  { name: 'MySQL', status: 'Connected' },
  { name: 'Redis', status: 'Connected' },
];

const QUEUE_DATA = [
  { queue: 'Design Curator', chain: 'Lidl GB', pending: 812, claimed: 34, drainRate: '~48/h', health: 'healthy' as const },
  { queue: 'Design Curator', chain: 'Lidl DE', pending: 445, claimed: 12, drainRate: '~31/h', health: 'attention' as const },
  { queue: 'Design Curator', chain: 'Morrisons', pending: 1203, claimed: 56, drainRate: '~62/h', health: 'healthy' as const },
  { queue: 'Design Review', chain: 'Lidl GB', pending: 234, claimed: 8, drainRate: '~22/h', health: 'healthy' as const },
  { queue: 'Design Review', chain: 'Lidl DE', pending: 89, claimed: 3, drainRate: '~14/h', health: 'healthy' as const },
  { queue: 'Design Review', chain: 'Morrisons', pending: 567, claimed: 21, drainRate: '~29/h', health: 'attention' as const },
  { queue: 'Uncertain Classification', chain: 'Lidl GB', pending: 156, claimed: 19, drainRate: '~41/h', health: 'healthy' as const },
  { queue: 'Uncertain Classification', chain: 'Lidl DE', pending: 2341, claimed: 4, drainRate: '~8/h', health: 'critical' as const },
  { queue: 'Uncertain Classification', chain: 'Morrisons', pending: 312, claimed: 15, drainRate: '~36/h', health: 'healthy' as const },
  { queue: 'FD Classification', chain: 'Lidl GB', pending: 42, claimed: 3, drainRate: '~8/h', health: 'healthy' as const },
  { queue: 'FD Classification', chain: 'Lidl DE', pending: 89, claimed: 1, drainRate: '~4/h', health: 'attention' as const },
  { queue: 'FD Classification', chain: 'Morrisons', pending: 31, claimed: 5, drainRate: '~12/h', health: 'healthy' as const },
];

// Per-chain store inference data (last 100 images or 1 hour, whichever comes first)
const STORE_INFERENCE: Record<string, { store: string; timespan: string; avgTop1: number; avgDelta: number }[]> = {
  'Lidl GB': [
    { store: 'Lidl Croydon #412', timespan: '100 imgs / 42min', avgTop1: 0.724, avgDelta: 0.089 },
    { store: 'Lidl Bow #318', timespan: '100 imgs / 51min', avgTop1: 0.781, avgDelta: 0.102 },
    { store: 'Lidl Leeds #221', timespan: '100 imgs / 38min', avgTop1: 0.856, avgDelta: 0.131 },
    { store: 'Lidl Manchester #155', timespan: '100 imgs / 44min', avgTop1: 0.891, avgDelta: 0.148 },
    { store: 'Lidl Edinburgh #307', timespan: '100 imgs / 55min', avgTop1: 0.912, avgDelta: 0.162 },
    { store: 'Lidl Glasgow #289', timespan: '100 imgs / 47min', avgTop1: 0.934, avgDelta: 0.178 },
    { store: 'Lidl Bristol #443', timespan: '78 imgs / 1h', avgTop1: 0.941, avgDelta: 0.185 },
    { store: 'Lidl Cardiff #512', timespan: '100 imgs / 39min', avgTop1: 0.952, avgDelta: 0.193 },
  ],
  'Lidl DE': [
    { store: 'Lidl Hamburg #87', timespan: '100 imgs / 46min', avgTop1: 0.834, avgDelta: 0.118 },
    { store: 'Lidl Berlin #53', timespan: '100 imgs / 35min', avgTop1: 0.878, avgDelta: 0.142 },
    { store: 'Lidl München #112', timespan: '100 imgs / 52min', avgTop1: 0.903, avgDelta: 0.156 },
    { store: 'Lidl Frankfurt #201', timespan: '100 imgs / 41min', avgTop1: 0.921, avgDelta: 0.167 },
    { store: 'Lidl Köln #88', timespan: '100 imgs / 49min', avgTop1: 0.945, avgDelta: 0.189 },
    { store: 'Lidl Stuttgart #145', timespan: '100 imgs / 58min', avgTop1: 0.958, avgDelta: 0.201 },
  ],
  'Morrisons': [
    { store: 'Morrisons Camden #201', timespan: '100 imgs / 37min', avgTop1: 0.798, avgDelta: 0.105 },
    { store: 'Morrisons Bristol #109', timespan: '100 imgs / 43min', avgTop1: 0.842, avgDelta: 0.127 },
    { store: 'Morrisons Manchester #88', timespan: '100 imgs / 50min', avgTop1: 0.903, avgDelta: 0.156 },
    { store: 'Morrisons Leeds #334', timespan: '100 imgs / 45min', avgTop1: 0.925, avgDelta: 0.171 },
    { store: 'Morrisons Liverpool #271', timespan: '85 imgs / 1h', avgTop1: 0.948, avgDelta: 0.191 },
  ],
  'Asda': [
    { store: 'Asda Leyton #44', timespan: '100 imgs / 53min', avgTop1: 0.761, avgDelta: 0.094 },
    { store: 'Asda Birmingham #67', timespan: '100 imgs / 40min', avgTop1: 0.872, avgDelta: 0.145 },
    { store: 'Asda Sheffield #119', timespan: '100 imgs / 48min', avgTop1: 0.918, avgDelta: 0.163 },
    { store: 'Asda Nottingham #203', timespan: '62 imgs / 1h', avgTop1: 0.961, avgDelta: 0.203 },
  ],
};

const HEALTH_BADGE: Record<string, { color: string; bg: string }> = {
  healthy: { color: 'var(--positive)', bg: 'var(--positive-subtle)' },
  attention: { color: 'var(--warning)', bg: 'var(--warning-subtle)' },
  critical: { color: 'var(--danger)', bg: 'var(--danger-subtle)' },
};

function anim(visible: boolean): React.CSSProperties {
  return {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(8px)',
    transition: 'opacity 350ms ease, transform 350ms ease',
  };
}

function computeInferenceStats(stores: { avgTop1: number; avgDelta: number }[]): ForgeStatCard[] {
  if (stores.length === 0) return [];
  const avgTop1 = stores.reduce((s, r) => s + r.avgTop1, 0) / stores.length;
  const avgDelta = stores.reduce((s, r) => s + r.avgDelta, 0) / stores.length;
  return [
    { label: 'Avg Top-1 Confidence', value: +avgTop1.toFixed(3), format: 'decimal' },
    { label: 'Avg Top1−Top2 Delta', value: +avgDelta.toFixed(3), format: 'decimal' },
  ];
}

export const LiveStatusDashboard: React.FC = () => {
  const [chain, setChain] = useState<Chain>(loadChain);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(() => new Date());
  const [storeSearch, setStoreSearch] = useState('');
  const visibility = useStaggeredEntrance(6, 60);

  const handleChainChange = useCallback((val: Chain) => {
    setChain(val);
    saveChain(val);
    setStoreSearch('');
  }, []);

  const handleRefresh = useCallback(() => {
    setLastRefreshed(new Date());
  }, []);

  const filteredQueues = chain === 'Global'
    ? QUEUE_DATA
    : QUEUE_DATA.filter((q) => q.chain === chain);

  // Inference stores: filter by selected chain, then by search query
  const chainStores = useMemo(() => {
    if (chain === 'Global') {
      return Object.values(STORE_INFERENCE).flat();
    }
    return STORE_INFERENCE[chain] || [];
  }, [chain]);

  const filteredStores = useMemo(() => {
    if (!storeSearch.trim()) return chainStores;
    const q = storeSearch.toLowerCase();
    return chainStores.filter(s => s.store.toLowerCase().includes(q));
  }, [chainStores, storeSearch]);

  const inferenceStats = useMemo(() => computeInferenceStats(chainStores), [chainStores]);

  const mono: React.CSSProperties = { fontFamily: 'var(--font-mono)' };

  return (
    <div className="page-content">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <ChainPicker value={chain} onChange={handleChainChange} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            Last Refreshed: {lastRefreshed.toLocaleTimeString()}
          </span>
          <button
            onClick={handleRefresh}
            style={{
              background: 'var(--elevated)', border: '1px solid var(--border)', borderRadius: 6,
              padding: '5px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
              color: 'var(--text-secondary)', fontSize: 12,
            }}
          >
            <RefreshOutlinedIcon style={{ fontSize: 16 }} /> Refresh
          </button>
        </div>
      </div>

      {/* 1. System Health */}
      <div style={anim(visibility[0])}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
          {SYSTEM_SERVICES.map((svc) => (
            <div key={svc.name} className="forge-card" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <CheckCircleOutlineIcon style={{ fontSize: 20, color: 'var(--positive)' }} />
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 2 }}>{svc.name}</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--positive)' }}>{svc.status}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 2. Queue Depths */}
      <div style={anim(visibility[1])}>
        <div className="forge-card" style={{ marginBottom: 20 }}>
          <div className="forge-card-title">Queue Depths</div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Queue</th>
                <th>Chain</th>
                <th>Pending</th>
                <th>Claimed</th>
                <th>Drain Rate</th>
                <th>Health</th>
              </tr>
            </thead>
            <tbody>
              {filteredQueues.map((row, i) => (
                <tr key={`${row.queue}-${row.chain}-${i}`}>
                  <td style={{ fontWeight: 600 }}>{row.queue}</td>
                  <td>{row.chain}</td>
                  <td style={mono}>{row.pending.toLocaleString()}</td>
                  <td style={mono}>{row.claimed}</td>
                  <td style={mono}>{row.drainRate}</td>
                  <td>
                    <span style={{
                      padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                      color: HEALTH_BADGE[row.health].color, background: HEALTH_BADGE[row.health].bg,
                    }}>
                      {row.health}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 3. Inference Health */}
      <div style={anim(visibility[2])}>
        <div className="forge-card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <div className="forge-card-title" style={{ marginBottom: 2 }}>Inference Health</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                Per store: last 100 images or last 1 hour, whichever comes first
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 8px',
              }}>
                <SearchOutlinedIcon style={{ fontSize: 14, color: 'var(--text-tertiary)' }} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={storeSearch}
                  onChange={(e) => setStoreSearch(e.target.value)}
                  style={{
                    background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)',
                    fontSize: 12, width: 120,
                  }}
                />
              </div>
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                {filteredStores.length} stores
              </span>
            </div>
          </div>
          <div className="stats-row" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: 16 }}>
            {inferenceStats.map((stat) => (
              <StatCard key={stat.label} stat={stat} isVisible={visibility[2]} />
            ))}
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Store</th>
                <th>Window</th>
                <th>Avg Top-1</th>
                <th>Avg Delta</th>
              </tr>
            </thead>
            <tbody>
              {filteredStores.map((row) => {
                const isLow = row.avgTop1 < 0.80;
                return (
                  <tr key={row.store}>
                    <td style={{ fontWeight: 600, color: isLow ? 'var(--danger)' : undefined }}>{row.store}</td>
                    <td style={mono}>{row.timespan}</td>
                    <td style={{ ...mono, color: isLow ? 'var(--danger)' : undefined, fontWeight: isLow ? 600 : undefined }}>
                      {row.avgTop1.toFixed(3)}
                    </td>
                    <td style={mono}>{row.avgDelta.toFixed(3)}</td>
                  </tr>
                );
              })}
              {filteredStores.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: 20 }}>
                    No stores match "{storeSearch}"
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
